import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { classifySetPasswordError } from '../../lib/authErrors';
import { AuthLayout } from './AuthLayout';
import { SetPasswordForm } from './SetPasswordForm';

/**
 * Set a new password from a reset link.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS PAGE FIRES NOTHING ON MOUNT. NOT A VALIDATION CALL, NOT A "IS THIS TOKEN
 * STILL GOOD" PROBE. NOTHING. DO NOT ADD ONE.
 *
 * That property is the entire reason a reset token can be strictly single-use.
 * Outlook Safe Links and Proofpoint detonate every URL in an inbound email
 * before the human ever clicks it — they issue a GET, headlessly, from a data
 * centre. If loading this page consumed or even touched the token, the scanner
 * would burn it and Joey's link would be dead on arrival. That is exactly what
 * happened to the magic link (§A1) and why THAT one had to be made replayable.
 *
 * The token is consumed only by the POST below, which carries a password a
 * scanner cannot invent. Same reasoning registered as §A5.
 *
 * `VerifyPage.tsx` sets the opposite precedent — it exchanges on mount because
 * it has to. Do not copy its shape here.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const token = params.get('token') ?? '';
  // Present only if the reset link carried it. Used solely to reject a password
  // containing the address — see the note in SetPasswordForm.
  const email = params.get('email') ?? '';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(password: string) {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      // `SetPasswordResponse` IS `SessionResponse` (shared contract): the reset
      // revokes every OTHER session and hands this caller a fresh one, so they
      // land in the app rather than retyping the password they just chose.
      const session = await api.resetPassword(token, password);
      await setSession(session.sessionToken);
      navigate('/', { replace: true });
    } catch (err) {
      setError(classifySetPasswordError(err, 'reset'));
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="This link is incomplete"
        subtitle="Coffman Excavation · Time & Materials portal"
      >
        <div className="auth-note">
          The reset link is missing its token — email clients sometimes break long links across
          lines. Request a fresh one and open it in a single click.
        </div>
        <div className="auth-links">
          <Link className="btn-link" to="/forgot">
            Request a new reset link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="This link works once. Pick something you'll actually remember."
    >
      <SetPasswordForm
        email={email}
        submitLabel="Set password"
        busyLabel="Saving…"
        busy={busy}
        error={error}
        onSubmit={(password) => void submit(password)}
      />
      <div className="auth-links">
        <Link className="btn-link" to="/login">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}
